import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  TemplateRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { NRMultiselectComponent } from '../nr-multiselect/nr-multiselect.component';
import { NRSingleselectComponent } from '../nr-singleselect/nr-singleselect.component';
import { NRTreeselectComponent } from '../nr-treeselect/nr-treeselect.component';
import { PrimaryFilterItem, IPrimaryFilterResponse } from './models/primary-filter.model';
import { ComponentTypeEnum } from '../../enums/component-type.enum';
import { INRSelectOption } from '../../interfaces/nr-select-option.interface';
import { cloneDeep } from 'lodash';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { RegionRouteService } from 'src/app/services/shared/nr-region-route.service';
import { take } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { ProfileResponse } from '@components/profile/Interfaces/profile';

@Component({
  selector: 'app-primary-filter',
  templateUrl: './primary-filter.component.html',
  styleUrls: ['./primary-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    NRSingleselectComponent,
    NRMultiselectComponent,
    NRTreeselectComponent,
    TooltipModule,
  ],
})
export class PrimaryFilterComponent implements AfterViewInit {
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<unknown>;
  @ViewChild('multiSelectDropdownTemplate')
  multiSelectDropdownTemplate!: TemplateRef<unknown>;
  @ViewChild('multiSelectTreeDropdownTemplate')
  multiSelectTreeDropdownTemplate!: TemplateRef<unknown>;
  @ViewChildren('multiSelectComp')
  multiSelectComp!: QueryList<NRMultiselectComponent>;

  @Output() applyEvt: EventEmitter<IPrimaryFilterResponse> =
    new EventEmitter<IPrimaryFilterResponse>();
  @Output() changeEvt: EventEmitter<IPrimaryFilterResponse> =
    new EventEmitter<IPrimaryFilterResponse>();

  @Input() showApplyActionBlock = true;
  @Input() applyDisabled = true;

  filterItems: PrimaryFilterItem[] = [];
  private filterItemsCopy: PrimaryFilterItem[] = [];
  private _dataSource: PrimaryFilterItem[] = [];
  private viewInitialized = false;
  private pendingDataSource: PrimaryFilterItem[] | null = null;
  isResetDisabled = true;
  private initialRegionId: number | null = null;
  private initialRouteId: number | null = null;
  private userProfileData!: ProfileResponse;

  constructor(
    private userService: UserAuthService,
    private regionRouteService: RegionRouteService,
  ) {}

  @Input() set dataSource(data: PrimaryFilterItem[]) {
    this._dataSource = data;

    if (this.viewInitialized) {
      this.updateFilterItems();
    } else {
      this.pendingDataSource = data;
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.pendingDataSource) {
      // Let Angular complete the current change detection cycle first
      setTimeout(() => {
        this.updateFilterItems();
        this.pendingDataSource = null;
      });
    }
  }

  private updateFilterItems(): void {
    if (!this._dataSource) return;
    this.setValidationFields(this._dataSource);
    this.setAccessibilityFields(this._dataSource);

    const clonedData = cloneDeep(this._dataSource);
    this.filterItems = this.prepareFilterData(this._dataSource);
    this.filterItemsCopy = clonedData;

    const ids = ['region', 'route'];
    ids.forEach((id, index) => {
      if (this.filterItems.length > index && this.filterItems[index].id === id) {
        const selected = this.filterItems[index].selectedItems;

        const isSelectedArray = Array.isArray(selected) && selected.length > 0;
        const isSelectedObject =
          selected && typeof selected === 'object' && !Array.isArray(selected);

        const isRequired = !(isSelectedArray || isSelectedObject);

        this.filterItems[index].validation.isRequired = isRequired;
        this.filterItems[index].isRequiredField = isRequired;
      }
    });

    this.userService.userProfileData$.pipe(take(1)).subscribe((userProfile) => {
      if (userProfile) {
        this.userProfileData = userProfile;
        this.initialRegionId = userProfile?.regionId;
        this.initialRouteId = userProfile.routeId;
      }
    });
  }

  private prepareFilterData(items: PrimaryFilterItem[]): PrimaryFilterItem[] {
    items.forEach((item) => {
      switch (item.type) {
        case ComponentTypeEnum.Dropdown:
          item.template = this.dropdownTemplate;
          break;
        case ComponentTypeEnum.MultiSelectDropdown:
          item.template = this.multiSelectDropdownTemplate;
          break;
        case ComponentTypeEnum.TreeDropdown:
          item.template = this.multiSelectTreeDropdownTemplate;
          break;
      }
    });
    return items;
  }

  onSelectItem(selectedList, item: PrimaryFilterItem) {
    item.selectedItems = selectedList;
    this.sendDataToParentOnSelection(item);
    if (item.id === 'route' && selectedList && selectedList.length > 0) {
      this.isResetDisabled = false;
    }
    this.checkRegionAndRouteSelection();
    this.checkForErrors(this.filterItems);
  }

  onClickApply() {
    const hasError: boolean = this.checkForErrors(this.filterItems);
    if (!hasError) {
      this.sendDataToParentOnApply();
    }
  }

  checkForErrors(items: PrimaryFilterItem[]): boolean {
    let errorFound = false;
    items.forEach((item) => {
      item.frmControl?.markAsDirty({
        onlySelf: true,
      });
      if (this.checkErrorForSingleItem(item)) {
        errorFound = true;
      }
    });
    return errorFound;
  }

  checkErrorForSingleItem(item: PrimaryFilterItem): boolean | undefined {
    if (item.validation) {
      item.hasError = false;
      //Required
      if (item.validation.isRequired) {
        if (!item.selectedItems) {
          item.hasError = true;
        } else if (item.selectedItems instanceof Array) {
          item.hasError = !(item.selectedItems as []).length;
        }
      }
      this.validateErrMsgAriaAttributes(item);
    }
    return item.hasError;
  }

  onClickReset() {
    this.filterItems.forEach((item, index) => {
      item.frmControl = new FormControl('');
      item.frmControl.setValue(item.selectedItems, { onlySelf: true });
      item.hasError = false;
      item.selectedItems =
        item.type === ComponentTypeEnum.Dropdown
          ? this.filterItemsCopy[index].selectedItems
          : Array.from(this.filterItemsCopy[index].selectedItems as Array<INRSelectOption>);

      if (item.hasDependency) {
        this.sendDataToParentOnSelection(item);
      }

      if (this.multiSelectComp.length > 0) {
        this.multiSelectComp.forEach((item) => {
          item.resetSelectAll(false);
        });
      }
    });

    // Set default Region and then Route
    if (this.userProfileData) {
      const regionId = this.userProfileData?.regionId;
      const routeId = this.userProfileData.routeId;
      const regionItem = this.filterItems.find((item) => item.id === 'region');
      let isRegionSingleSelect = false;
      let isRouteSingleSelect = false;

      if (regionItem) {
        isRegionSingleSelect = regionItem.type === ComponentTypeEnum.Dropdown;

        regionItem.selectedItems = isRegionSingleSelect ? { id: regionId } : [regionId];

        this.sendDataToParentOnSelection(regionItem);
      }

      setTimeout(() => {
        const routeItem = this.filterItems.find((item) => item.id === 'route');
        if (routeItem) {
          // routeItem.selectedItems = [routeId];
          isRouteSingleSelect = routeItem.type === ComponentTypeEnum.Dropdown;

          routeItem.selectedItems = isRouteSingleSelect ? { id: routeId } : [routeId];

          this.sendDataToParentOnSelection(routeItem);
        }
        this.isResetDisabled = true;
        this.applyEvt.emit({
          selectedItems: [],
          applyDisabled: true,
        });
      }, 100);

      if (isRegionSingleSelect && isRouteSingleSelect) {
        this.regionRouteService.loadSetRegionRoute(regionId, routeId);
      }
    }
  }

  private getSelectedData(): PrimaryFilterItem[] {
    const data: PrimaryFilterItem[] = [];
    this.filterItems.forEach((item) => {
      if (item.selectedItems) {
        data.push(item);
      }
    });
    return data;
  }

  private sendDataToParentOnApply() {
    const data: IPrimaryFilterResponse = {
      selectedItems: this.getSelectedData(),
    };
    this.applyEvt.emit(data);
  }

  private sendDataToParentOnSelection(selectedItem: PrimaryFilterItem) {
    const data: IPrimaryFilterResponse = {
      selectedItems: this.filterItems,
      changedItem: selectedItem,
    };
    this.changeEvt.emit(data);
    const idx = this.filterItems.findIndex((f) => f.id === selectedItem.id);
    if (idx !== -1) {
      const selected = selectedItem.selectedItems;

      const isSelectedArray = Array.isArray(selected) && selected.length > 0;
      const isSelectedObject = selected && typeof selected === 'object' && !Array.isArray(selected);

      const isRequired = !(isSelectedArray || isSelectedObject);

      this.filterItems[idx].validation.isRequired = isRequired;
      this.filterItems[idx].isRequiredField = isRequired;
    }
    if (this.multiSelectComp.length > 0) {
      this.multiSelectComp.forEach((item: any) => {
        if (item.configData?.isDependent) {
          if (item.configData?.data.length <= 0) {
            item.resetSelectAll(false);
          } else if (item.configData?.data.length !== item.configData?.selectedItems.length) {
            item.resetSelectAll(false);
          } else {
            item.resetSelectAll(true);
          }
        }
      });
    }
  }

  private setValidationFields(data: PrimaryFilterItem[]) {
    data.forEach((item) => {
      if (item.validation) {
        item.isRequiredField = item.validation.isRequired;
        item.frmControl = new FormControl('');
        item.validation.isRequiredMsg =
          item.validation.isRequiredMsg ?? 'This is a required field.';
      }
    });
  }

  private setAccessibilityFields(data: PrimaryFilterItem[]) {
    data.forEach((item) => {
      item.ariaLabel = item.ariaLabel ?? item.label;
    });
  }

  private getSelector(data: PrimaryFilterItem) {
    let selector = '';
    if (data.type === ComponentTypeEnum.Dropdown) {
      selector = `#${data.id} div > span[role="combobox"]`;
    } else if (data.type === ComponentTypeEnum.MultiSelectDropdown) {
      selector = `#${data.id} .p-hidden-accessible > input[role="combobox"]`;
    } else if (data.type === ComponentTypeEnum.TreeDropdown) {
      selector = `#${data.id} .p-hidden-accessible > input[type="text"]`;
    }
    return selector;
  }

  private validateErrMsgAriaAttributes(data: PrimaryFilterItem) {
    const selector = this.getSelector(data);
    const inputElement = document.querySelector(selector);
    const val = data.hasError ? `${data.id}ErrMsg` : '';
    inputElement?.setAttribute('aria-describedby', val);
  }

  private checkRegionAndRouteSelection() {
    const selectedRegion = this.filterItems.find((item) => item.id === 'region')?.selectedItems;
    const selectedRoute = this.filterItems.find((item) => item.id === 'route')?.selectedItems;

    // Narrow and compare selectedRegion
    const isRegionSame =
      Array.isArray(selectedRegion) &&
      selectedRegion.length === 1 &&
      selectedRegion[0] === this.initialRegionId;

    // Narrow and compare selectedRoute
    const isRouteSame =
      Array.isArray(selectedRoute) &&
      selectedRoute.length === 1 &&
      selectedRoute[0] === this.initialRouteId;
    this.isResetDisabled = isRegionSame && isRouteSame;
  }

  shouldShowTooltip(filterItem): boolean {
    const selected = filterItem.selectedItems || [];
    return selected.length > 1;
  }

  getSelectedLabels(filterItem): string {
    const selectedIds = filterItem.selectedItems || [];
    const options = filterItem.data || [];

    const selectedLabels = options
      .filter((opt) => selectedIds.includes(opt.id))
      .map((opt) => opt.name)
      .join(', ');

    return selectedLabels;
  }
}
